import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../../features/auth/services/auth.service';
import { environment } from '../../../environments/environment';

// Global state for token refresh
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

function shouldAddToken(url: string): boolean {
  // Don't add token to requests outside our API (e.g. R2 presigned upload URL)
  if (!url.startsWith(environment.apiHost)) {
    return false;
  }
  // Don't add token to public endpoints (no auth required)
  const publicEndpoints = [
    '/auth/login',
    '/auth/refresh',
    '/users/signup',
    '/users/recover-password',
    '/users/validate-recover-password-code',
    '/users/reset-password',
    '/users/confirm-email',
    '/users/collaborative-signup',
    '/organizations/invitations/by-token'
  ];

  // Check if the URL contains any public endpoint
  const isPublicEndpoint = publicEndpoints.some(endpoint =>
    url.includes(endpoint)
  );

  return !isPublicEndpoint;
}

function addTokenToRequest(req: any, token: string): any {
  const authHeader = `Bearer ${token}`;

  return req.clone({
    setHeaders: {
      Authorization: authHeader
    }
  });
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const accessToken = authService.getAccessToken();

  // Add token to the requests (except for public endpoints)
  if (shouldAddToken(req.url)) {
    // If no token or empty token and this is a protected endpoint, don't proceed
    if (!accessToken || accessToken.trim() === '') {
      authService.logout();
      return throwError(() => new Error('No authentication token available'));
    }

    // Clone request with token
    const clonedRequest = addTokenToRequest(req, accessToken);

    // Verify token was added (for debugging)
    const authHeader = clonedRequest.headers.get('Authorization');

    if (!authHeader || !authHeader.startsWith('Bearer ')) {
      return throwError(() => new Error('Failed to add authentication token to request'));
    }

    req = clonedRequest;
  } else {
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // If it's a 401 error and we have a token, try to refresh
      if (error.status === 401 && accessToken && shouldAddToken(req.url)) {
        return handle401Error(req, next, authService);
      }

      // If it's a 401 error without a token or on a public endpoint, redirect to login
      if (error.status === 401) {
        authService.logout();
        return throwError(() => error);
      }

      return throwError(() => error);
    })
  );
};

function handle401Error(req: any, next: any, authService: AuthService): Observable<any> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = authService.getRefreshToken();

    if (!refreshToken) {
      isRefreshing = false;
      authService.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    return authService.refreshAccessToken().pipe(
      switchMap((newAccessToken: string) => {
        isRefreshing = false;
        refreshTokenSubject.next(newAccessToken);
        return next(addTokenToRequest(req, newAccessToken));
      }),
      catchError((error) => {
        isRefreshing = false;
        authService.logout();
        return throwError(() => error);
      })
    );
  } else {
    // If already refreshing, wait for it to finish and try again
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => {
        return next(addTokenToRequest(req, token!));
      })
    );
  }
}
